/*
 * Copyright 2026 North Carolina State University
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Tab, Tabs,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TuneIcon from '@mui/icons-material/Tune';
import { toast } from 'react-toastify';
import { ipmApi, errorMessage, invalidateLookup } from '../api';
import EntryForm from './EntryForm';
import EntriesList from './EntriesList';
import FormBuilder from './FormBuilder';

/**
 * A page built on admin-defined forms: tabs for each form, the form itself
 * (inline, or in a popup), that form's entries, and the form builder.
 * Data Entry and List of Controls are both this, with different settings.
 */
const FormWorkspace = ({
  kind = 'entry',
  title,
  subtitle,
  emptyText,
  starterLabel,
  entryInDialog = false,
  allColumns = false,
}) => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [building, setBuilding] = useState(false);
  const [adding, setAdding] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [addingStarter, setAddingStarter] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = (await ipmApi.forms(kind)).filter(f => f.active !== false);
      setForms(list);
      setSelectedId(prev => (list.some(f => f._id === prev) ? prev : list[0]?._id || null));
    } catch (err) {
      toast.error(errorMessage(err, 'Could not load forms'));
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  const addStarter = async () => {
    setAddingStarter(true);
    try {
      await ipmApi.createStarterForms(kind);
      await load();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not add the starter forms'));
    } finally {
      setAddingStarter(false);
    }
  };

  // Entries on the List of Controls feed the Control pickers elsewhere.
  const changed = () => {
    setRefreshKey(k => k + 1);
    if (kind === 'controls') invalidateLookup('controls');
  };

  if (building) {
    return (
      <FormBuilder kind={kind} initialFormId={selectedId} backLabel={`Back to ${title}`}
        onClose={() => { setBuilding(false); load(); }} onChanged={load} />
    );
  }

  const form = forms.find(f => f._id === selectedId);

  return (
    <div>
      <Box className="ipm-page-header ipm-builder-header">
        <Box>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button variant="outlined" startIcon={<TuneIcon />} onClick={() => setBuilding(true)}>
            {kind === 'controls' ? 'Manage form' : 'Manage forms'}
          </Button>
          {entryInDialog && form && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAdding(true)}>Add entry</Button>
          )}
        </Box>
      </Box>

      {loading && <CircularProgress />}

      {!loading && forms.length === 0 && (
        <Alert severity="info" action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button color="inherit" size="small" onClick={() => setBuilding(true)}>Create a form</Button>
            <Button color="inherit" size="small" onClick={addStarter} disabled={addingStarter}>{starterLabel}</Button>
          </Box>
        }>
          {emptyText}
        </Alert>
      )}

      {forms.length > 0 && (
        <>
          {forms.length > 1 && (
            <Tabs value={selectedId || false} onChange={(e, v) => setSelectedId(v)} variant="scrollable" sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
              {forms.map(f => <Tab key={f._id} value={f._id} label={f.name} />)}
            </Tabs>
          )}
          {form && (
            <>
              {form.description && <p className="ipm-muted" style={{ marginTop: 0 }}>{form.description}</p>}
              {!entryInDialog && (
                // key: a fresh form state per form, and again whenever the form definition changes
                <EntryForm key={`${form._id}-${form.version}`} form={form} onSaved={changed} />
              )}
              <EntriesList form={form} refreshKey={refreshKey} allColumns={allColumns} onChanged={changed}
                title={entryInDialog ? (forms.length > 1 ? form.name : 'All entries') : undefined} />
            </>
          )}
        </>
      )}

      <Dialog open={adding} onClose={() => setAdding(false)} maxWidth="md" fullWidth>
        <DialogTitle>New {form?.name} entry</DialogTitle>
        <DialogContent dividers>
          {form && adding && (
            <EntryForm form={form} onCancel={() => setAdding(false)}
              onSaved={() => { setAdding(false); changed(); }} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FormWorkspace;
